import { defineComponent as _defineComponent } from 'vue'
import { offset, flip } from '@floating-ui/vue'
import { twMerge } from 'tailwind-merge'
import { computed, ComputedRef, nextTick, ref, useListeners, watch } from 'vue'

import LButton from '@/components/core/Button.vue'
import DeleteModal from '@/components/core/DeleteModal.vue'
import DropdownMenuItem from '@/components/core/DropdownMenuItem.vue'
import WaypointListModal from '@/components/trip/travelPlan/waypoint/WaypointListModal.vue'
import { usePopover, useDismiss, unwrapElement } from '@/composables/popover'
import { tripQueryKeys, useSelectedTrip } from '@/composables/trip'
import LTextPrompt from '@/global-components/LTextPrompt.vue'
import DocumentModule from '@/store/document'
import PhotoModule from '@/store/photo'
import { Document } from '@/types/document'
import { Photo } from '@/types/photo'
import { GridContentType, TextFieldRules, DocumentType } from '@/utils/enums'
import { useQueryClient } from '@/composables/vue-query'

export interface GridMenuProps {
  showWaypointLink?: boolean
  contentType: GridContentType
  storeModule: DocumentModule | PhotoModule
  file: Document | Photo
  twClass?: string | string[]
}


export default /*#__PURE__*/_defineComponent({
  __name: 'GridMenu',
  props: {
    showWaypointLink: { type: Boolean, default: true },
    contentType: null,
    storeModule: null,
    file: null,
    twClass: { default: undefined }
  },
  emits: ["unlink"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'unlink', id: string): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as { showWaypointLink: boolean, contentType: GridContentType, storeModule: DocumentModule | PhotoModule, file: Document | Photo, twClass: string | string[] };





const listeners = useListeners()
const trip = useSelectedTrip()
const queryClient = useQueryClient()

const textPromptRef = ref<InstanceType<typeof LTextPrompt> | null>(null)
const waypointListModalRef = ref<InstanceType<typeof WaypointListModal> | null>(
  null,
)

const translationPrefix = computed(() => {
  if (props.file?.type === DocumentType.DIRECTORY) {
    return props.file?.type
  } else {
    return props.contentType
  }
})

const showDeleteModal = ref(false)

watch(showDeleteModal, (newValue) => {
  if (!newValue) {
    focusReferenceRef()
  }
})

function focusReferenceRef() {
  nextTick(() => {
    const referenceElement = unwrapElement(referenceRef.value)
    if (referenceElement instanceof HTMLElement) {
      referenceElement.focus()
    }
  })
}

// Available Menu Options ------------------------------------------------------

const allOptions = [
  {
    title: 'download',
    operation: () => {
      props.storeModule.setFile(props.file)
      props.storeModule.download()
      isOpen.value = false
    },
  },
  {
    title: 'rename',
    operation: () => {
      textPromptRef.value?.toggleDialog()
      isOpen.value = false
    },
  },
  {
    title: 'delete',
    operation: () => {
      showDeleteModal.value = true
      isOpen.value = false
    },
  },
]

const options: ComputedRef<{ title: string; operation: () => void }[]> =
  computed(() => {
    const availableOptions = []

    if (props.file.type === DocumentType.DIRECTORY) {
      return allOptions.filter((el) => ['rename', 'delete'].includes(el.title))
    }

    if (props.contentType === GridContentType.PHOTO) {
      availableOptions.push(...allOptions.filter((el) => el.title !== 'rename'))
    } else {
      availableOptions.push(...allOptions)
    }

    if (props.file.type === DocumentType.FILE) {
      if (props.file.reference && props.showWaypointLink) {
        availableOptions.splice(availableOptions.length - 1, 0, {
          title: 'unlinkFromWaypoint',
          operation: async () => {
            await updateFile({ reference: null, onModel: null })
            isOpen.value = false
          },
        })
      } else if (props.showWaypointLink) {
        availableOptions.splice(availableOptions.length - 1, 0, {
          title: 'linkToWaypoint',
          operation: () => {
            waypointListModalRef.value?.toggleDialog()
            isOpen.value = false
          },
        })
      } else if (listeners.unlink) {
        availableOptions.splice(availableOptions.length - 1, 0, {
          title: 'unlinkFromWaypoint',
          operation: () => {
            if (props.file._id) {
              emit('unlink', props.file._id)
            }
            isOpen.value = false
          },
        })
      }
    }

    return availableOptions
  })

// Popover logic ---------------------------------------------------------------

const isOpen = ref(false)

const { referenceRef, floatingRef, floatingStyles } = usePopover({
  placement: 'bottom-end',
  isOpen,
  middleware: [offset(4), flip({ padding: 16 })],
})
useDismiss({ referenceRef, floatingRef, isOpen })

// Operations ------------------------------------------------------------------

async function linkToWaypoint(waypointId: string) {
  waypointListModalRef.value?.toggleDialog()

  await updateFile({
    reference: waypointId,
    onModel: 'Waypoint',
  })
}

async function updateFile(params: {
  originName?: string
  reference?: string | null
  onModel?: string | null
}) {
  await props.storeModule.update({
    id: trip.value.id,
    fileId: props.file._id,
    params,
  })

  await queryClient.invalidateQueries(tripQueryKeys.summary(trip.value.id))
}

async function deleteFile() {
  await props.storeModule.delete({
    id: trip.value.id,
    fileId: props.file._id,
  })

  await queryClient.invalidateQueries(tripQueryKeys.summary(trip.value.id))
}

return { __sfc: true,props, emit, listeners, trip, queryClient, textPromptRef, waypointListModalRef, translationPrefix, showDeleteModal, focusReferenceRef, allOptions, options, isOpen, referenceRef, floatingRef, floatingStyles, linkToWaypoint, updateFile, deleteFile, twMerge, LButton, DeleteModal, DropdownMenuItem, WaypointListModal, LTextPrompt, TextFieldRules }
}

})